import { Link } from "react-router-dom";
import { Check, ArrowUpRight, Star } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { CurrencyToggle } from "@/components/Navbar";
import Testimonials from "@/components/Testimonials";
import { cn } from "@/lib/utils";
import { KEYWORDS, FAQ } from "@/lib/seoContent";
import {
  webPageLd,
  breadcrumbLd,
  organizationLd,
  servicePackageLd,
  faqPageLd,
} from "@/lib/structuredData";

const Pricing = () => {
  const { t, lang, formatPrice } = useApp();
  useSEO({
    title: t.pricing.metaTitle,
    description: t.pricing.metaDesc,
    path: "/pricing",
    lang,
    keywords: KEYWORDS[lang].pricing,
    jsonLd: [
      organizationLd(),
      webPageLd({
        path: "/pricing",
        name: t.pricing.metaTitle,
        description: t.pricing.metaDesc,
        lang,
      }),
      ...t.pricing.packages.map((p) =>
        servicePackageLd({
          name: p.name,
          description: p.tagline,
          price: p.priceEUR,
          currency: "EUR",
        }),
      ),
      breadcrumbLd([
        { name: t.nav.home, path: "/" },
        { name: t.nav.pricing, path: "/pricing" },
      ]),
      faqPageLd(FAQ[lang]),
    ],
  });

  const pkgs = t.pricing.packages;

  return (
    <main data-testid="pricing-page" className="pt-16 lg:pt-20">
      {/* HEAD */}
      <section>
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-20 lg:pt-28 pb-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
              <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
              {t.pricing.eyebrow}
            </div>
            <h1
              data-testid="pricing-title"
              className="mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[0.95] tracking-tighter font-black text-[#0A0A0A] font-[Cabinet_Grotesk]"
            >
              {t.pricing.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base sm:text-lg text-[#525252]">
              {t.pricing.lead}
            </p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A] mb-3">
              Currency
            </div>
            <CurrencyToggle />
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="pb-4">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 border-t border-l border-[#0A0A0A]">
            {pkgs.map((p) => {
              const highlight = !!p.highlight;
              return (
                <article
                  key={p.id}
                  data-testid={`pricing-tier-${p.id}`}
                  className={cn(
                    "relative border-r border-b border-[#0A0A0A] p-8 lg:p-10 flex flex-col",
                    highlight
                      ? "bg-[#0A0A0A] text-white"
                      : "bg-white text-[#0A0A0A]",
                  )}
                >
                  {highlight && (
                    <div
                      data-testid="best-value-badge"
                      className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-[#3A00FF] text-white text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1"
                    >
                      <Star className="h-3 w-3" aria-hidden />
                      {t.pricing.badgeBestValue}
                    </div>
                  )}
                  <div className={cn("text-xs tracking-[0.2em] uppercase font-bold", highlight ? "text-[#3A00FF]" : "text-[#3A00FF]")}>
                    {p.id}
                  </div>
                  <h3 className="mt-3 text-2xl lg:text-3xl tracking-tight font-black font-[Cabinet_Grotesk]">
                    {p.name}
                  </h3>
                  <p className={cn("mt-2 text-sm", highlight ? "text-white/70" : "text-[#525252]")}>
                    {p.tagline}
                  </p>

                  <div className="mt-8 flex items-baseline gap-2">
                    <div
                      data-testid={`price-${p.id}`}
                      className="text-5xl lg:text-6xl font-black tracking-tighter font-[Cabinet_Grotesk]"
                    >
                      {formatPrice(p)}
                    </div>
                    <div className={cn("text-xs tracking-[0.2em] uppercase font-bold", highlight ? "text-white/60" : "text-[#525252]")}>
                      one-time
                    </div>
                  </div>

                  <ul className="mt-8 space-y-3 flex-1">
                    {p.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <Check
                          className={cn(
                            "h-4 w-4 mt-0.5 shrink-0",
                            highlight ? "text-[#3A00FF]" : "text-[#3A00FF]",
                          )}
                          aria-hidden
                        />
                        <span className={highlight ? "text-white/85" : "text-[#0A0A0A]"}>
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10">
                    <Link to="/contact">
                      <Button
                        data-testid={`select-${p.id}`}
                        className={cn(
                          "w-full h-12 rounded-none text-xs tracking-widest uppercase font-bold",
                          highlight
                            ? "bg-[#3A00FF] text-white hover:bg-white hover:text-[#0A0A0A]"
                            : "bg-[#0A0A0A] text-white hover:bg-[#3A00FF]",
                        )}
                      >
                        {t.pricing.cta}
                        <ArrowUpRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="border-t border-[#0A0A0A] bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex items-center gap-3 text-xs tracking-[0.2em] uppercase font-bold text-[#0A0A0A]">
            <span className="inline-block h-[1px] w-8 bg-[#3A00FF]" />
            Compare
          </div>
          <h2 className="mt-4 max-w-3xl text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight font-black text-[#0A0A0A] font-[Cabinet_Grotesk]">
            {t.pricing.compareTitle}
          </h2>

          <div className="mt-12 border border-[#0A0A0A] overflow-x-auto bg-white">
            <table className="w-full text-left border-collapse min-w-[720px]">
              <thead>
                <tr className="border-b border-[#0A0A0A] bg-[#F3F3F3]">
                  <th className="p-4 lg:p-6 text-xs tracking-[0.2em] uppercase font-bold w-1/3">Feature</th>
                  {pkgs.map((p) => (
                    <th
                      key={p.id}
                      className={cn(
                        "p-4 lg:p-6 text-xs tracking-[0.2em] uppercase font-bold border-l border-[#0A0A0A]",
                        p.highlight && "bg-[#0A0A0A] text-white",
                      )}
                    >
                      {p.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.pricing.compareRows.map((row, i) => (
                  <tr
                    key={i}
                    data-testid={`compare-row-${i}`}
                    className="border-b border-[#0A0A0A]/20"
                  >
                    <td className="p-4 lg:p-6 text-sm font-bold text-[#0A0A0A]">
                      {row.label}
                    </td>
                    {row.values.map((v, j) => (
                      <td
                        key={j}
                        className={cn(
                          "p-4 lg:p-6 text-sm border-l border-[#0A0A0A]/20",
                          pkgs[j].highlight && "bg-[#0A0A0A] text-white",
                        )}
                      >
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="p-4 lg:p-6 text-xs tracking-[0.2em] uppercase font-bold">Price</td>
                  {pkgs.map((p) => (
                    <td
                      key={p.id}
                      className={cn(
                        "p-4 lg:p-6 text-lg font-black font-[Cabinet_Grotesk] border-l border-[#0A0A0A]/20",
                        p.highlight && "bg-[#0A0A0A] text-white",
                      )}
                    >
                      {formatPrice(p)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Testimonials
        eyebrow={t.home.testimonialsEyebrow}
        title={t.home.testimonialsTitle}
      />
    </main>
  );
};

export default Pricing;
